import type { StrapiBlock } from "@/types/strapi";

const formatDate = (date: Date) => {
	return date.toISOString().split("T")[0];
};

const formatStringToDate = (dateString: string) => {
	return new Date(dateString).toLocaleDateString("en-US", {
		month: "short",
		day: "numeric",
		year: "numeric",
	});
};

const formatDisplayDate = (dateString: string) => {
	if (!dateString) return "Select due date";
	const date = new Date(dateString);
	return date.toLocaleDateString("en-US", {
		month: "long",
		day: "numeric",
		year: "numeric",
	});
};

const getDaysInMonth = (date: Date) => {
	return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
};

const getFirstDayOfMonth = (date: Date) => {
	return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
};

const buttonColorClass = (color: string) => {
	switch (color) {
		case "white":
			return "bg-white";
		case "green":
			return "bg-newTheme-lime";
		case "blue":
			return "bg-newTheme-lightBlue";
		default:
			return "bg-newTheme-sky";
	}
};

const formatTime = (dateString: string) => {
	const date = new Date(dateString);
	const now = new Date();
	const diff = now.getTime() - date.getTime();
	const days = Math.floor(diff / (1000 * 60 * 60 * 24));

	if (days === 0) {
		return date.toLocaleTimeString("en-US", {
			hour: "numeric",
			minute: "2-digit",
			hour12: true,
		});
	} else if (days === 1) {
		return "Yesterday";
	} else if (days < 7) {
		return date.toLocaleDateString("en-US", { weekday: "short" });
	} else {
		return date.toLocaleDateString("en-US", {
			month: "short",
			day: "numeric",
		});
	}
};

const formatCurrency = (amount: number) => {
	return new Intl.NumberFormat("en-US", {
		style: "currency",
		currency: "USD",
		minimumFractionDigits: 0,
		maximumFractionDigits: 0,
	}).format(amount);
};

const monthsSelectArray = [
	{ key: "january", short: "Jan" },
	{ key: "february", short: "Feb" },
	{ key: "march", short: "Mar" },
	{ key: "april", short: "Apr" },
	{ key: "may", short: "May" },
	{ key: "june", short: "Jun" },
	{ key: "july", short: "Jul" },
	{ key: "august", short: "Aug" },
	{ key: "september", short: "Sep" },
	{ key: "october", short: "Oct" },
	{ key: "november", short: "Nov" },
	{ key: "december", short: "Dec" },
];

const getMonthsArray = [
	{ key: "january", label: "January", short: "Jan" },
	{ key: "february", label: "February", short: "Feb" },
	{ key: "march", label: "March", short: "Mar" },
	{ key: "april", label: "April", short: "Apr" },
	{ key: "may", label: "May", short: "May" },
	{ key: "june", label: "June", short: "Jun" },
	{ key: "july", label: "July", short: "Jul" },
	{ key: "august", label: "August", short: "Aug" },
	{
		key: "september",
		label: "September",
		short: "Sep",
	},
	{ key: "october", label: "October", short: "Oct" },
	{ key: "november", label: "November", short: "Nov" },
	{ key: "december", label: "December", short: "Dec" },
];

// Flatten Strapi Blocks into a plain string for the existing renderer
const blocksToText = (blocks: StrapiBlock[] | null | undefined): string => {
	if (!blocks?.length) return "";
	return blocks.flatMap((block) => block.children.map((c) => c.text)).join(" ");
};

// turn sting to unique id
const stringToUniqueId = (str: string): string => {
	const timestamp = Date.now().toString(36); // Convert current timestamp to base36
	const randomNum = Math.floor(Math.random() * 1000000).toString(36); // Generate a random number and convert to base36
	const uniqueId = `${str}-${timestamp}-${randomNum}`; // Combine the string, timestamp, and random number
	return uniqueId;
};

export {
	formatDate,
	formatDisplayDate,
	getDaysInMonth,
	getFirstDayOfMonth,
	buttonColorClass,
	formatTime,
	formatCurrency,
	formatStringToDate,
	monthsSelectArray,
	getMonthsArray,
	blocksToText,
	stringToUniqueId,
};
